class CartTimer extends HTMLElement {
  constructor() {
    super();

    this.timerContainerElement = this.querySelector("[data-cart-timer-container]");
    this.timerElement = this.querySelector("[data-cart-timer]"); // Format: <span data-cart-timer>00:00</span>
    this.cartBtnTextElement = this.querySelector("[data-cart-btn-text]");
    this.cartTimerMinutes = Number(this.dataset.cartTimerMinutes);
    this.cartHasItems = false;
    this.timeRemaining = 0;
    this.timerInterval = null;

    this.cartCountElement = this.querySelector("[data-cart-count]");
    this.cartCount = 0;

    this.modal = document.querySelector("#cart-timer-modal");
    this.modalContentKey = "ajax-cart-timer-modal";
    this.modalCloseBtn = this.modal?.querySelector("modal-trigger[data-action='close']") || null;
    this.modalRemoveAllBtn = this.modal?.querySelector("[data-modal-remove-all-btn]") || null;
    this.modalKeepAllBtn = this.modal?.querySelector("[data-modal-keep-all-btn]") || null;

    this.init();
  }

  init() {
    if (this.timerContainerElement && this.timerElement && this.cartTimerMinutes > 0) {
      this.initTimer();
    }

    this.initCartUpdateListener();

    // Close modal if click outside (when modal is open)
    const self = this;
    window.addEventListener("click", (e) => {
      const modalContent = e.target.closest(".js-modal-content");
      const modal = e.target.closest("#cart-timer-modal");
      
      // Only execute if clicking on the modal but not on its content
      if (modal && !modalContent) {
        // Restart timer with new time
        self.setEndTimer(true);
        self.startTimer();

        self.modal.close();
      }
    });
  }

  async initTimer() {
    await this.checkCartItems();
    this.setEndTimer();
    this.startTimer();
    this.classList.add('loaded');
  }

  async checkCartItems(onUpdate = false) {
    await fetch('/cart.js')
      .then(response => response.json())
      .then(data => {
        if (data.item_count > 0) {
          this.cartHasItems = true;
        } else {
          this.cartHasItems = false;
        }

        if (onUpdate === true && data.item_count > this.cartCount) {
          // Restart timer with new time
          this.setEndTimer(true);
        }

        this.cartCount = data.item_count;
        this.setCartCount();
      })
      .catch(error => {
        console.error('Error fetching cart data:', error);
      });
  }

  setEndTimer(restart = false) {
    if (restart === true || (!this.localEndTimer && this.cartHasItems === true && this.cartTimerMinutes > 0)) {
      const endTimer = new Date().getTime() + this.cartTimerMinutes * 60 * 1000;
      this.localEndTimer = new Date(endTimer).toISOString();
    } else if (this.localEndTimer && this.cartHasItems === false) {
      this.localEndTimer = '';
      this.hideTimer();
    }
  }

  startTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }

    if (this.cartHasItems === true && this.localEndTimer) {
      const endTimer = new Date(this.localEndTimer);
      if (isNaN(endTimer.getTime())) return;

      const now = new Date();
      this.timeRemaining = endTimer - now < 0 ? 0 : endTimer - now;

      this.setTimer();
      this.showTimer();

      if (this.timeRemaining > 0) {
        this.timerInterval = setInterval(() => {
          if (this.timeRemaining > 0) {
            this.setTimer();
          } else {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
            // Open modal to keep products in cart or not
            if (this.cartHasItems === true) {
              this.openModal();
            }
          }
        }, 1000);

      } else {
        this.openModal();
      }
    }
  }

  setTimer() {
    if (this.timeRemaining > 0) {
      const endTimer = new Date(this.localEndTimer);
      const now = new Date();
      this.timeRemaining = endTimer - now < 0 ? 0 : endTimer - now;
    } else {
      this.timeRemaining = 0;
    }
    
    const minutes = Math.floor(this.timeRemaining / (1000 * 60));
    const seconds = Math.floor((this.timeRemaining % (1000 * 60)) / 1000);
    this.timerElement.textContent = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  showTimer() {
    this.cartBtnTextElement.classList.add('jc-hidden');
    this.timerContainerElement.classList.remove('jc-hidden');
    this.classList.add('active');
  }

  hideTimer() {
    this.cartBtnTextElement.classList.remove('jc-hidden');
    this.timerContainerElement.classList.add('jc-hidden');
    this.classList.remove('active');
  }

  initCartUpdateListener() {
    // Intercept fetch cart ('/cart/add.js', '/cart/update.js', '/cart/change.js', '/cart/clear.js')
    // Guard against multiple instances wrapping window.fetch more than once
    if (window.__cartTimerFetchWrapped) {
      window.__cartTimerInstances = window.__cartTimerInstances || [];
      window.__cartTimerInstances.push(this);
      return;
    }

    window.__cartTimerFetchWrapped = true;
    window.__cartTimerInstances = [this];

    const originalFetch = window.fetch;

    window.fetch = async function(input, init) {
      const url = typeof input === 'string' ? input : input.url;

      const isCartRequest = [
        '/cart/add.js',
        '/cart/update.js',
        '/cart/change.js',
        '/cart/clear.js'
      ].some(endpoint => url.includes(endpoint));

      if (isCartRequest) {
        const response = await originalFetch.apply(this, arguments);

        // Wait for the response to be completed
        const clonedResponse = response.clone();
        clonedResponse.json().then(() => {
          (window.__cartTimerInstances || []).forEach(instance => instance.onCartUpdate());
        })
        .catch(error => {
          console.error('Error fetching cart data:', error);
        });

        return response;
      }

      return originalFetch.apply(this, arguments);
    };
  }

  async onCartUpdate() {
    await this.checkCartItems(true);
    this.setEndTimer();
    this.startTimer();
  }

  setCartCount() {
    if (this.cartCountElement) {
      if (this.cartCount > 0) {
        this.cartCountElement.classList.remove('jc-hidden');
      } else {
        this.cartCountElement.classList.add('jc-hidden');
      }
      this.cartCountElement.textContent = this.cartCount;
    }
  }

  async openModal() {
    if (this.modal) {
      document.querySelector("#cart-timer-content").appendChild(this.modal);
      await this.modal.openFetchModal(this.modalContentKey);

      this.modalCloseBtn = this.modal?.querySelector("modal-trigger[data-action='close']") || null;
      this.modalRemoveAllBtn = this.modal?.querySelector("[data-modal-remove-all-btn]") || null;
      this.modalKeepAllBtn = this.modal?.querySelector("[data-modal-keep-all-btn]") || null;

      this.addCloseBtnListener();
      this.addRemoveAllBtnListener();
      this.addKeepAllBtnListener();
    } else {
      console.warn("Modal not found");
    }
  }

  addCloseBtnListener() {
    if (this.modalCloseBtn) {
      const self = this;
      this.modalCloseBtn.addEventListener("click", (e) => {
        e.stopPropagation();

        // Restart timer with new time
        self.setEndTimer(true);
        self.startTimer();

        self.modal.close();
      });
    }
  }

  addRemoveAllBtnListener() {
    if (this.modalRemoveAllBtn) {
      const self = this;
      this.modalRemoveAllBtn.addEventListener("click", (e) => {
        e.stopPropagation();

        fetch('/cart/clear.js', {
          method: 'POST'
        })
        .then(response => response.json())
        .then(data => {
          document.documentElement.dispatchEvent(
            new CustomEvent("cart:rerender", {
              detail: {
                cart: { item_count: data.item_count, items: data.items },
                scrollToTop: false,
              },
            })
          );
        })
        .catch(error => {
          console.error('Error clearing cart:', error);
        });

        self.modal.close();
      });
    }
  }

  addKeepAllBtnListener() {
    if (this.modalKeepAllBtn) {
      const self = this;
      this.modalKeepAllBtn.addEventListener("click", (e) => {
        e.stopPropagation();

        // Restart timer with new time
        self.setEndTimer(true);
        self.startTimer();

        self.modal.close();
      });
    }
  }

  set localEndTimer(value) {
    localStorage.setItem("cartEndTimer", value);
  }

  get localEndTimer() {
    return localStorage.getItem("cartEndTimer");
  }
}

if (!customElements.get("cart-timer")) {
  customElements.define("cart-timer", CartTimer);
}
